import { ErrorBoundary } from '@ecomm/error-handling'
import { VideoBanner } from '@ecomm/monitoring/components'
import { schema as videoBannerSchema } from '@ecomm/monitoring/components/VideoBanner/schema'
import { safeParseJSONDataFromContentful } from '@ecomm/scout'
import { AlarmMoments } from '@ecomm/shared-components'
import type { FeatureFlag } from '@ecomm/shared-ninetailed'
import { Experience, safeMapExperiences } from '@ecomm/shared-ninetailed'
import { useOdmonVideoDogAdExperience } from '@ecomm/shared-ninetailed-odmon'
import * as O from 'fp-ts/lib/Option'
import { pipe } from 'fp-ts/lib/function'
import { useMemo } from 'react'
import { odmonAlarmMomentsTabHeaders } from '../BelowTheFold/constants'
import type { LanderBelowTheFoldSchema } from '../BelowTheFold/schema'
import { useOdmonDogVideoBannerExperienceProps } from '../ExperimentUtils/useOdmonDogVideoBannerExperienceProps'

function PlaceholderComponent() {
  return <div className="w-[1px] h-[1px]" />
}

type Props = {
  readonly data: LanderBelowTheFoldSchema
}

export function VideoBannerExperiment({ data }: Props) {
  const isOdmonVideoDogAdExperience = useOdmonVideoDogAdExperience().isVariant
  const experienceData = useOdmonDogVideoBannerExperienceProps()

  const videoBannerData = pipe(
    safeParseJSONDataFromContentful(data, 'videoBanner'),
    O.chain(json => (!isOdmonVideoDogAdExperience ? O.some(json) : O.none)),
    O.chain(json => {
      const data = videoBannerSchema.safeParse(json)
      return data.success ? O.some(data.data) : O.none
    })
  )

  const videoBannerExperimentData = pipe(
    safeParseJSONDataFromContentful(data, 'videoBannerExperiment'),
    O.chain(json => (isOdmonVideoDogAdExperience ? O.some(json) : O.none)),
    O.chain(json => {
      const data = videoBannerSchema.safeParse(json)
      return data.success ? O.some(data.data) : O.none
    })
  )

  const experiences = useMemo(
    () =>
      safeMapExperiences<FeatureFlag['data']>({
        nt_experiences: experienceData.nt_experiences
      }),
    [experienceData]
  )

  return (
    <ErrorBoundary>
      <Experience
        component={PlaceholderComponent}
        experiences={experiences}
        id={experienceData.id}
      />
      {pipe(
        videoBannerExperimentData,
        O.fold(
          () => null,
          data => (
            <div data-testid="variant-experience">
              <VideoBanner data={data} />
            </div>
          )
        )
      )}
      {data.alarmMoments ? (
        <AlarmMoments
          {...data.alarmMoments}
          tabsHeaders={odmonAlarmMomentsTabHeaders}
        />
      ) : null}
      {pipe(
        videoBannerData,
        O.fold(
          () => null,
          data => (
            <div data-testid="control-experience">
              <VideoBanner data={data} />
            </div>
          )
        )
      )}
    </ErrorBoundary>
  )
}
